import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { ToastrModule } from 'ngx-toastr';
import { environment as env } from 'src/environments/environment';
import { ApiDocumentComponent } from './api-document/api-document.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { EventListComponent } from './notification-engine/event-list/event-list.component';
import { NotificationEngineComponent } from './notification-engine/notification-engine.component';
import { CustomBtnComponent } from './notification-engine/shared/custom-btn/custom-btn.component';
import { SubscriptionDashboardComponent } from './notification-engine/subscription-dashboard/subscription-dashboard.component';
import { SubscriptionFormComponent } from './notification-engine/subscription-form/subscription-form.component';
import { UserListComponent } from './notification-engine/user-list/user-list.component';
import { ConstantService } from './shared/services/constant-service/constant.service';
import { DataService } from './shared/services/data-service/data.service';
import { SharedModule } from './shared/shared.module';
import { CreateSupplierProfileDetailComponent } from './supplier-profile/create-supplier-profile-detail/create-supplier-profile-detail.component';
import { CreateSupplierProfileComponent } from './supplier-profile/create-supplier-profile/create-supplier-profile.component';
import { BreadcrumbsComponent } from './supplier-profile/shared/breadcrumbs/breadcrumbs.component';
import { ExceptionTableRowComponent } from './supplier-profile/shared/exception-table-row/exception-table-row.component';
import { ExceptionTableComponent } from './supplier-profile/shared/exception-table/exception-table.component';
import { MaterialInputComponent } from './supplier-profile/shared/material-input/material-input.component';
import { PlantInputComponent } from './supplier-profile/shared/plant-input/plant-input.component';
import { VendorInputComponent } from './supplier-profile/shared/vendor-input/vendor-input.component';
import { SupplierProfileComponent } from './supplier-profile/supplier-profile.component';
import { ViewSupplierProfileDetailComponent } from './supplier-profile/view-supplier-profile-detail/view-supplier-profile-detail.component';
import { ViewSupplierProfileComponent } from './supplier-profile/view-supplier-profile/view-supplier-profile.component';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1; // Remove this line to use Angular Universal

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: env.client_id,
      authority:
        'https://login.microsoftonline.com/d1ee1acd-bc7a-4bc4-a787-938c49a83906',
      redirectUri: env.app_url,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/', ['user.read']);
  protectedResourceMap.set(env.base_api_url, [env.base_api_scope]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    SupplierProfileComponent,
    CreateSupplierProfileComponent,
    ExceptionTableComponent,
    ExceptionTableRowComponent,
    ViewSupplierProfileComponent,
    ViewSupplierProfileDetailComponent,
    BreadcrumbsComponent,
    CreateSupplierProfileDetailComponent,
    PlantInputComponent,
    VendorInputComponent,
    ApiDocumentComponent,
    NotificationEngineComponent,
    SubscriptionFormComponent,
    CustomBtnComponent,
    SubscriptionDashboardComponent,
    UserListComponent,
    EventListComponent,
    MaterialInputComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MsalModule,
    HttpClientModule,
    SharedModule,
    FormsModule,
    AutocompleteLibModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    DataService,
    ConstantService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
